/** Layout constants for mission panel safe-zone calculations (renderer + tests). */
export const MISSION_PANEL_WIDTH_PX = 320;
export const MISSION_PANEL_RIGHT_MARGIN_PX = 16;
export const MISSION_PANEL_TOP_OFFSET_PX = 72;
export const MISSION_PANEL_INSET_RIGHT_PX =
  MISSION_PANEL_WIDTH_PX + MISSION_PANEL_RIGHT_MARGIN_PX;

/** Above tour spotlight overlay (modal + 1200) so the mission panel stays interactive. */
export const MISSION_PANEL_Z_INDEX = 1280;

/** Above tour overlay; matches SkipConfirmationDialog stacking. */
export const TOUR_MISSION_DIALOG_Z_INDEX_OFFSET = 1300;

export interface SafeViewportRect {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export interface ScrollIntoSafeViewportOptions {
  panelInsetRight?: number;
  headerInsetTop?: number;
  minVisibleRatio?: number;
}

export function getMissionPanelInset(): number {
  return MISSION_PANEL_INSET_RIGHT_PX;
}

export function computeSafeViewportRect(
  viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1280,
  viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800,
  options: ScrollIntoSafeViewportOptions = {},
): SafeViewportRect {
  const panelInsetRight = options.panelInsetRight ?? MISSION_PANEL_INSET_RIGHT_PX;
  const headerInsetTop = options.headerInsetTop ?? MISSION_PANEL_TOP_OFFSET_PX;
  const left = 0;
  const top = headerInsetTop;
  const right = Math.max(0, viewportWidth - panelInsetRight);
  const bottom = viewportHeight;
  return {
    top,
    left,
    right,
    bottom,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
}

export function rectIntersectsSafeViewport(
  rect: Pick<DOMRect, 'top' | 'left' | 'right' | 'bottom' | 'width' | 'height'>,
  safe: SafeViewportRect,
  minVisibleRatio = 0.5,
): boolean {
  if (rect.width <= 0 || rect.height <= 0) {
    return false;
  }
  const overlapLeft = Math.max(rect.left, safe.left);
  const overlapTop = Math.max(rect.top, safe.top);
  const overlapRight = Math.min(rect.right, safe.right);
  const overlapBottom = Math.min(rect.bottom, safe.bottom);
  const overlapWidth = Math.max(0, overlapRight - overlapLeft);
  const overlapHeight = Math.max(0, overlapBottom - overlapTop);
  const overlapArea = overlapWidth * overlapHeight;
  const elementArea = rect.width * rect.height;
  return overlapArea / elementArea >= minVisibleRatio;
}

export function scrollElementIntoSafeViewport(
  element: HTMLElement,
  options: ScrollIntoSafeViewportOptions = {},
): void {
  const minVisibleRatio = options.minVisibleRatio ?? 0.5;
  element.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' });

  const safe = computeSafeViewportRect(
    window.innerWidth,
    window.innerHeight,
    options,
  );

  let rect = element.getBoundingClientRect();
  if (rectIntersectsSafeViewport(rect, safe, minVisibleRatio)) {
    return;
  }

  if (rect.right > safe.right) {
    window.scrollBy({
      top: 0,
      left: rect.right - safe.right + 24,
      behavior: 'instant',
    });
    rect = element.getBoundingClientRect();
  }

  if (rect.left < safe.left) {
    window.scrollBy({
      top: 0,
      left: rect.left - safe.left - 16,
      behavior: 'instant',
    });
    rect = element.getBoundingClientRect();
  }

  if (rect.top < safe.top) {
    window.scrollBy({
      top: rect.top - safe.top - 16,
      left: 0,
      behavior: 'instant',
    });
    rect = element.getBoundingClientRect();
  }

  if (rect.bottom > safe.bottom) {
    window.scrollBy({
      top: rect.bottom - safe.bottom + 16,
      left: 0,
      behavior: 'instant',
    });
  }
}
